'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'

// Insight categories - used for filtering and subscription preferences
const categories = [
  { id: 'all', label: 'All Insights' },
  { id: 'market', label: 'Market Trends' },
  { id: 'artist', label: 'Artist Spotlight' },
  { id: 'regional', label: 'Regional Focus' },
  { id: 'risk', label: 'Risk Alerts' },
  { id: 'opportunity', label: 'Opportunities' },
]

const insights = [
  {
    id: 1,
    category: 'market',
    tag: 'Market Shift',
    tagColor: 'var(--accent-primary)',
    headline: 'Asian collectors driving Contemporary surge',
    body: 'Cross-border bidding from Hong Kong and Singapore has increased 34% YoY, with particular strength in works by female artists under 45. This trend is reshaping price dynamics in the Contemporary sector.',
    metric: '+34%',
    metricLabel: 'Cross-border bidding YoY',
    metricColor: 'var(--positive)',
    date: '2024-02-06',
    confidence: 94,
    source: 'Auction data analysis',
    relatedArtists: ['Christine Ay Tjoe', 'Jadé Fadojutimi', 'Flora Yukhnovich'],
    relatedCategories: ['Contemporary', 'Paintings'],
    featured: true,
  },
  {
    id: 2,
    category: 'opportunity',
    tag: 'Emerging Trend',
    tagColor: 'var(--accent-secondary)',
    headline: 'Photography market reaches inflection point',
    body: 'After five years of steady growth, vintage photography is showing signs of mainstream acceptance with record auction results in Q4. Institutional buyers entering the market.',
    metric: '$8.2M',
    metricLabel: 'Q4 record sale',
    metricColor: 'var(--accent-warm)',
    date: '2024-02-05',
    confidence: 87,
    source: 'Market pattern detection',
    relatedArtists: ['Richard Avedon', 'Helmut Newton', 'Cindy Sherman'],
    relatedCategories: ['Photography', 'Contemporary'],
    featured: false,
  },
  {
    id: 3,
    category: 'risk',
    tag: 'Risk Alert',
    tagColor: 'var(--accent-deep)',
    headline: 'Post-War sector showing cooling signals',
    body: 'Buy-in rates have climbed to 28% for works estimated above $5M, suggesting price resistance at the top end of the market. Consider adjusting valuations accordingly.',
    metric: '28%',
    metricLabel: 'Buy-in rate >$5M',
    metricColor: 'var(--negative)',
    date: '2024-02-04',
    confidence: 91,
    source: 'Sell-through analysis',
    relatedArtists: ['Gerhard Richter', 'Sigmar Polke', 'Georg Baselitz'],
    relatedCategories: ['Post-War', 'Paintings'],
    featured: false,
  },
  {
    id: 4,
    category: 'artist',
    tag: 'Artist Spotlight',
    tagColor: 'var(--positive)',
    headline: 'Amoako Boafo auction debut exceeds expectations',
    body: 'The Ghanaian painter\'s first major auction season saw 100% sell-through with an average hammer of 3.2x high estimate. Secondary market momentum building rapidly.',
    metric: '3.2×',
    metricLabel: 'Avg vs high estimate',
    metricColor: 'var(--positive)',
    date: '2024-02-03',
    confidence: 96,
    source: 'Artist performance tracking',
    relatedArtists: ['Toyin Ojih Odutola', 'Njideka Akunyili Crosby', 'Lynette Yiadom-Boakye'],
    relatedCategories: ['Contemporary', 'Paintings'],
    featured: false,
  },
  {
    id: 5,
    category: 'regional',
    tag: 'Regional Focus',
    tagColor: 'var(--accent-warm)',
    headline: 'Middle East emerges as new auction hub',
    body: 'Dubai and Abu Dhabi auction volumes up 67% as regional collectors increasingly bid locally. Christie\'s and Sotheby\'s expanding MENA presence.',
    metric: '+67%',
    metricLabel: 'MENA auction volume',
    metricColor: 'var(--positive)',
    date: '2024-02-02',
    confidence: 89,
    source: 'Regional market analysis',
    relatedArtists: ['eL Seed', 'Shirin Neshat', 'Ahmed Mater'],
    relatedCategories: ['Contemporary', 'Mixed Media'],
    featured: false,
  },
  {
    id: 6,
    category: 'opportunity',
    tag: 'Price Signal',
    tagColor: 'var(--accent-primary)',
    headline: 'Undervalued: Latin American Modernists',
    body: 'AI analysis identifies systematic underpricing of Latin American Modernist works relative to comparable European pieces. 40% price gap suggests arbitrage opportunity.',
    metric: '40%',
    metricLabel: 'Price gap vs peers',
    metricColor: 'var(--accent-warm)',
    date: '2024-02-01',
    confidence: 82,
    source: 'Comparative valuation model',
    relatedArtists: ['Wifredo Lam', 'Roberto Matta', 'Rufino Tamayo'],
    relatedCategories: ['Modern', 'Paintings'],
    featured: false,
  },
]

function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function AIInsights() {
  const [activeCategory, setActiveCategory] = useState('all')
  const [savedInsights, setSavedInsights] = useState<number[]>([])

  const filteredInsights = activeCategory === 'all'
    ? insights
    : insights.filter(i => i.category === activeCategory)

  const featuredInsight = insights.find(i => i.featured)
  const regularInsights = filteredInsights.filter(i => !i.featured || activeCategory !== 'all')

  const toggleSave = (id: number) => {
    setSavedInsights(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  return (
    <section className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header - left aligned */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-2">
              Data Driven Insights
            </h2>
            <p className="text-text-secondary max-w-xl">
              Pattern recognition across 340 million data points. Signals that matter,
              delivered in context.
            </p>
          </div>

          {/* Subscribe CTA */}
          <button
            className="pill flex items-center gap-2 shrink-0"
            style={{ background: 'var(--accent-primary)', color: 'white' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Subscribe to alerts
          </button>
        </div>

        {/* AI Signal - Condensed */}
        <div className="card p-4 mb-8 flex items-center gap-4 border-l-2" style={{ borderLeftColor: 'var(--accent-primary)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-medium">AI Signal:</span>{' '}
              <span className="text-text-secondary">6 new signals detected this week. Asian collector activity up 34%, photography reaching inflection point.</span>
            </p>
          </div>
          <Link href="/get-started" className="text-xs text-accent-primary hover:underline flex-shrink-0">
            Subscribe →
          </Link>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? 'bg-text-primary text-white'
                  : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Featured insight - only show on "All" */}
        {activeCategory === 'all' && featuredInsight && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="card p-8 mb-8 relative overflow-hidden"
          >
            {/* Featured badge */}
            <div
              className="absolute top-0 left-0 right-0 h-1"
              style={{ background: featuredInsight.tagColor }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: featuredInsight.tagColor }}
                  >
                    {featuredInsight.tag}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {formatDate(featuredInsight.date)}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-page text-text-tertiary">
                    {featuredInsight.confidence}% confidence
                  </span>
                </div>

                <h3 className="font-display text-3xl font-light mb-4 leading-tight">
                  {featuredInsight.headline}
                </h3>

                <p className="text-text-secondary leading-relaxed mb-6">
                  {featuredInsight.body}
                </p>

                {/* Related tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {featuredInsight.relatedArtists.map((artist) => (
                    <span key={artist} className="text-xs px-3 py-1 rounded-full bg-page border border-border-subtle text-text-secondary">
                      {artist}
                    </span>
                  ))}
                  {featuredInsight.relatedCategories.map((cat) => (
                    <span key={cat} className="text-xs px-3 py-1 rounded-full border border-border text-text-tertiary">
                      {cat}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4">
                  <button
                    className="text-sm font-medium hover:underline"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    Read full analysis →
                  </button>
                  <button
                    onClick={() => toggleSave(featuredInsight.id)}
                    className="flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
                  >
                    <svg className="w-4 h-4" fill={savedInsights.includes(featuredInsight.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    Save
                  </button>
                  <button className="flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share
                  </button>
                </div>
              </div>

              {/* Metric highlight */}
              <div className="flex flex-col justify-center items-center lg:items-end text-center lg:text-right">
                <div
                  className="text-6xl font-display font-light tabular-nums mb-2"
                  style={{ color: featuredInsight.metricColor }}
                >
                  {featuredInsight.metric}
                </div>
                <p className="text-text-tertiary text-sm">
                  {featuredInsight.metricLabel}
                </p>
                <p className="text-text-muted text-xs mt-2">
                  Source: {featuredInsight.source}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Insight cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {regularInsights.map((insight, i) => (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              viewport={{ once: true }}
              className="group relative card p-6 transition-all duration-300 hover:shadow-lg"
            >
              {/* Top accent line on hover */}
              <div
                className="absolute top-0 left-6 right-6 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"
                style={{ background: insight.tagColor }}
              />

              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: insight.tagColor }}
                  >
                    {insight.tag}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {formatDate(insight.date)}
                </span>
              </div>

              {/* Headline */}
              <h3 className="font-display text-xl font-light mb-3 leading-tight">
                {insight.headline}
              </h3>

              {/* Body */}
              <p className="text-text-secondary text-sm leading-relaxed mb-4">
                {insight.body}
              </p>

              {/* Related tags */}
              <div className="flex flex-wrap gap-1 mb-4">
                {insight.relatedCategories.slice(0, 2).map((cat) => (
                  <span key={cat} className="text-xs px-2 py-0.5 rounded-full border border-border-subtle text-text-tertiary">
                    {cat}
                  </span>
                ))}
              </div>

              {/* Metric */}
              <div className="pt-4 border-t border-border-subtle">
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <div
                      className="text-3xl font-display font-light tabular-nums"
                      style={{ color: insight.metricColor }}
                    >
                      {insight.metric}
                    </div>
                    <p className="text-text-tertiary text-xs mt-1">
                      {insight.metricLabel}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-page text-text-tertiary">
                    {insight.confidence}%
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-4">
                  <button
                    className="text-sm font-medium hover:underline"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    Deep dive →
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleSave(insight.id)}
                      className="text-text-tertiary hover:text-text-secondary transition-colors"
                    >
                      <svg className="w-4 h-4" fill={savedInsights.includes(insight.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </button>
                    <button className="text-text-tertiary hover:text-text-secondary transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTAs */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-text-secondary text-sm mb-1">
                Get insights delivered to your inbox
              </p>
              <p className="text-text-tertiary text-xs">
                Choose your categories: Market Trends, Artist Alerts, Risk Signals, and more
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/explore/insights"
                className="pill"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                View all insights
              </Link>
              <Link
                href="/get-started"
                className="pill flex items-center gap-2"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-standard)',
                  color: 'var(--text-primary)'
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Subscribe to digest
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
